// src/components/admin/scheduling/components/EditSessionModal.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { FiFileText } from "react-icons/fi";
import "./Modal.css";
import { backdropDismiss } from "../../../../utils/backdropDismiss";

import { SESSION_STATUSES, WEEKDAY_BLOCKS, SATURDAY_BLOCKS } from "../constants";
import { formatLongDate, minutesToDate, isSameDay } from "../dateUtils";
import { buildTimeOptions, formatTimeFromMinutes, minutesFromDate } from "../timeUtils";
import { fetchAvailabilityBlocks, fetchScheduledSessions, fetchDayConfigsForDates } from "../schedulingApi";
import SubjectSelect from "./SubjectSelect";
import {
  dayKeyFromDate as dayKeyFromScheduleDate,
  resolveScheduleForDate,
} from "../scheduleSettingsUtils";
import ModalCalendarPanel from "../student_calendar/ModalCalendarPanel";

// --- HELPERS ---
function dayKeyFromDate(d) {
  return dayKeyFromScheduleDate(d);
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

function isCanceledStatus(status) {
  return String(status || "").toLowerCase().includes("cancel");
}

function toMinutes(value) {
  return minutesFromDate(new Date(value));
}

function maxConcurrentWithinRange(existingSessions, startMin, endMin, excludeSessionId = null) {
  const events = [];

  for (const session of existingSessions || []) {
    if (excludeSessionId && session.id === excludeSessionId) continue;
    if (isCanceledStatus(session.status)) continue;

    const sessionStart = toMinutes(session.start);
    const sessionEnd = toMinutes(session.end);
    if (!overlaps(sessionStart, sessionEnd, startMin, endMin)) continue;

    const overlapStart = Math.max(sessionStart, startMin);
    const overlapEnd = Math.min(sessionEnd, endMin);
    if (overlapEnd <= overlapStart) continue;

    events.push({ minute: overlapStart, delta: 1 });
    events.push({ minute: overlapEnd, delta: -1 });
  }

  events.sort((a, b) => {
    if (a.minute !== b.minute) return a.minute - b.minute;
    return a.delta - b.delta; // Process end(-1) before start(+1) at same minute.
  });

  let active = 0;
  let peak = 0;
  for (const event of events) {
    active += event.delta;
    if (active > peak) peak = active;
  }

  return peak;
}

function resolveLane(existingSessions, startMin, endMin, excludeSessionId, tutorUid) {
  const entries = (existingSessions || [])
    .filter((session) => {
      if (session.id === excludeSessionId) return false;
      if (tutorUid && session.tutorUid !== tutorUid) return false;
      if (isCanceledStatus(session.status)) return false;

      const sessionStart = toMinutes(session.start);
      const sessionEnd = Math.max(sessionStart + 5, toMinutes(session.end));
      return overlaps(sessionStart, sessionEnd, startMin, endMin);
    })
    .map((session) => ({
      id: session.id,
      startMin: toMinutes(session.start),
      endMin: Math.max(toMinutes(session.start) + 5, toMinutes(session.end)),
    }));

  entries.push({
    id: "__candidate__",
    startMin,
    endMin,
  });

  entries.sort((a, b) => {
    if (a.startMin !== b.startMin) return a.startMin - b.startMin;
    if (a.endMin !== b.endMin) return a.endMin - b.endMin;
    if (a.id === "__candidate__") return 1;
    if (b.id === "__candidate__") return -1;
    return 0;
  });

  const laneEnds = [-Infinity, -Infinity, -Infinity];
  const laneById = new Map();

  for (const entry of entries) {
    let lane = null;
    for (let idx = 0; idx < 3; idx += 1) {
      if (laneEnds[idx] <= entry.startMin) {
        lane = idx;
        break;
      }
    }

    if (lane == null) {
      if (entry.id === "__candidate__") return null;
      lane = laneEnds.indexOf(Math.min(...laneEnds));
    }

    laneEnds[lane] = Math.max(laneEnds[lane], entry.endMin);
    laneById.set(entry.id, lane);
  }

  return laneById.get("__candidate__") ?? null;
}

function canFitTutor(existingSessions, tutorUid, startMin, endMin, excludeSessionId) {
  const tutorSessions = (existingSessions || []).filter((s) => s.tutorUid === tutorUid);
  const existingPeak = maxConcurrentWithinRange(tutorSessions, startMin, endMin, excludeSessionId);
  return existingPeak < 3;
}

function seatsLeftForTutor(existingSessions, tutorUid, startMin, endMin, excludeSessionId) {
  const tutorSessions = (existingSessions || []).filter((s) => s.tutorUid === tutorUid);
  const existingPeak = maxConcurrentWithinRange(tutorSessions, startMin, endMin, excludeSessionId);
  return Math.max(0, 3 - existingPeak);
}

function isFullyContainedInAvailability(blocks, start, end) {
  for (const block of blocks || []) {
    if (block.unavailable) continue;
    
    const blockStart = new Date(block.start);
    const blockEnd = new Date(block.end);
    if (blockStart <= start && blockEnd >= end) {
      return true;
    }
  }
  return false;
}

function defaultBlocksForDayKey(dayKey) {
  return dayKey === "saturday" ? SATURDAY_BLOCKS : WEEKDAY_BLOCKS;
}

function resolveBlockIdForStartMinute(timeBlocks, dayKey, startMin, fallbackBlockId) {
  const blocks = Array.isArray(timeBlocks) && timeBlocks.length ? timeBlocks : defaultBlocksForDayKey(dayKey);
  if (!Array.isArray(blocks) || blocks.length === 0) return fallbackBlockId || "";

  const exact = blocks.find((block) => startMin >= block.startMin && startMin < block.endMin);
  if (exact?.id) return exact.id;

  const nearest = [...blocks].sort((a, b) => {
    const distA = Math.abs(startMin - a.startMin);
    const distB = Math.abs(startMin - b.startMin);
    return distA - distB;
  })[0];

  return nearest?.id || fallbackBlockId || "";
}

// =========================================
// 1. REUSABLE SESSION EDITOR COMPONENT
// =========================================
export function SessionEditor({
  initialSession,
  students,
  tutors,
  onSave,
  onCreate = null,
  onDelete,
  onCancel,
  onDraftChange = null,
  isNested = false,
  isMasterMode = false,
  masterAvailability = [],
  masterDayConfigs = {},
  allWeekSessions = [],
  isReadOnly = false,
  // Admin-only: opens the session log workspace for this session in a new tab.
  // Only rendered when a handler is supplied, so other embedders of the editor
  // (e.g. the Student Calendar modal) are unaffected.
  onOpenSessionLog = null,
  hasSessionLog = false,
}) {
  // --- Form State ---
  const [studentId, setStudentId] = useState("");
  const [status, setStatus] = useState("Pending");
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [dateStr, setDateStr] = useState("");
  const [startMin, setStartMin] = useState(0);
  const [endMin, setEndMin] = useState(0);
  const [tutorUid, setTutorUid] = useState("");
  const [dayAvailability, setDayAvailability] = useState([]);
  const [daySessions, setDaySessions] = useState([]);
  const [isDayClosed, setIsDayClosed] = useState(false);
  const [dayTimeBlocks, setDayTimeBlocks] = useState([]);
  const [dayRange, setDayRange] = useState({ startMin: 8 * 60, endMin: 22 * 60 });
  const isHydratingFromInitialSessionRef = useRef(false);
  const isCreateMode = !initialSession?.id && typeof onCreate === "function";
  const conflictExclusionSessionId = isCreateMode ? null : initialSession?.id;

  // 1. Initialize State
  useEffect(() => {
    if (!initialSession) return;
    isHydratingFromInitialSessionRef.current = true;
    const s = initialSession;

    setStudentId(s.studentId || "");
    
    setStatus(s.status || "Pending");
    setSubject(s.subject || "");
    setNote(s.note || "");

    const d = s.start ? new Date(s.start) : new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0"); 
    setDateStr(`${yyyy}-${mm}-${dd}`);

    setTutorUid(s.tutorUid || "");

    const startDate = s.start ? new Date(s.start) : new Date(d);
    const endDate = s.end ? new Date(s.end) : new Date(startDate.getTime() + 60 * 60 * 1000);
    setStartMin(minutesFromDate(startDate));
    setEndMin(Math.max(minutesFromDate(startDate) + 5, minutesFromDate(endDate)));
  }, [initialSession, students]);

  // 2. Fetch Data When Date Changes
  useEffect(() => {
    let alive = true;
    const fetchDayData = async () => {
      if (!dateStr) return;
      
      const [y, m, d] = dateStr.split("-").map(Number);
      const startOfDay = new Date(y, m - 1, d);
      
      if (isMasterMode) {
         // Use the session's explicit dayKey if available, otherwise derive
         const dk = initialSession?.dayKey || dayKeyFromDate(startOfDay);
         
         const cfg = masterDayConfigs[dk];
         const resolved = resolveScheduleForDate(startOfDay, cfg);
         if (alive) {
           setIsDayClosed(!!cfg?.isClosed);
           setDayRange({ startMin: resolved.startMin, endMin: resolved.endMin });
           setDayTimeBlocks(Array.isArray(cfg?.timeBlocks) && cfg.timeBlocks.length ? cfg.timeBlocks : defaultBlocksForDayKey(dk));
         }

         const relevantSessions = allWeekSessions.filter(s => isSameDay(new Date(s.start), startOfDay));
         if (alive) setDaySessions(relevantSessions);

         const relevantAvail = masterAvailability.filter(a => a.dayKey === dk && !a.unavailable);
         const transformedAvail = relevantAvail.map(ma => ({
            uid: ma.uid,
            start: minutesToDate(startOfDay, ma.startMin),
            end: minutesToDate(startOfDay, ma.endMin)
         }));

         if (alive) setDayAvailability(transformedAvail);
         return; 
      }

      const endOfDay = new Date(y, m - 1, d, 23, 59, 59);
      const [avail, sess, cfgs] = await Promise.all([
        fetchAvailabilityBlocks({ rangeStart: startOfDay, rangeEnd: endOfDay }),
        fetchScheduledSessions({ rangeStart: startOfDay, rangeEnd: endOfDay }),
        fetchDayConfigsForDates([startOfDay])
      ]);

      if (alive) {
        setDayAvailability(avail);
        setDaySessions(sess);
        const config = cfgs[dateStr];
        const resolved = resolveScheduleForDate(startOfDay, config);
        setIsDayClosed(!!resolved.isClosed);
        setDayRange({ startMin: resolved.startMin, endMin: resolved.endMin });
        const fetchedDayKey = dayKeyFromDate(startOfDay) || initialSession?.dayKey || "monday";
        setDayTimeBlocks(
          Array.isArray(config?.timeBlocks) && config.timeBlocks.length
            ? config.timeBlocks
            : defaultBlocksForDayKey(fetchedDayKey)
        );
      }
    };

    fetchDayData();
    return () => { alive = false; };
  }, [dateStr, isMasterMode, masterAvailability, masterDayConfigs, allWeekSessions, initialSession?.dayKey]);

  // --- Calculations ---
  const dateObj = useMemo(() => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  }, [dateStr]);

  // ✅ FIX: In Master Mode, trust initialSession.dayKey explicitly. 
  // This prevents timezone shifts (e.g. Fri vs Sat) from breaking the layout.
  const dayKey = useMemo(() => {
    if (isMasterMode && initialSession.dayKey) {
      return initialSession.dayKey;
    }
    return dateObj ? dayKeyFromDate(dateObj) : null;
  }, [dateObj, isMasterMode, initialSession]);

  const selectedStudent = useMemo(
    () => students.find((s) => s.id === studentId) || null,
    [students, studentId]
  );

  const availableTutors = useMemo(() => {
    if (isDayClosed) return []; 
    if (!dateObj) return [];
    
    const start = minutesToDate(dateObj, startMin);
    const end = minutesToDate(dateObj, endMin);

    return tutors.map(t => {
      const tutorBlocks = dayAvailability.filter(a => a.uid === t.id);
      
      const isTimeAvailable = isFullyContainedInAvailability(tutorBlocks, start, end);
      if (!isTimeAvailable) return null;

      const canFit = canFitTutor(daySessions, t.id, startMin, endMin, conflictExclusionSessionId);
      if (!canFit) return null;

      const seatsLeft = seatsLeftForTutor(daySessions, t.id, startMin, endMin, conflictExclusionSessionId);

      return { ...t, seatsLeft };
    }).filter(Boolean);

  }, [tutors, dayAvailability, daySessions, dateObj, startMin, endMin, conflictExclusionSessionId, isDayClosed]);

  const timeOptions = useMemo(() => {
    const opts = buildTimeOptions(dayRange.startMin, Math.max(dayRange.startMin, dayRange.endMin - 5), 5);
    if (!opts.includes(startMin)) opts.push(startMin);
    return opts.sort((a, b) => a - b);
  }, [dayRange, startMin]);

  const endOptions = useMemo(() => {
    const minEnd = Math.min(dayRange.endMin, startMin + 5);
    const opts = buildTimeOptions(minEnd, dayRange.endMin, 5);
    if (!opts.includes(endMin)) opts.push(endMin);
    return opts.sort((a, b) => a - b).filter((value) => value > startMin);
  }, [dayRange, startMin, endMin]);

  useEffect(() => {
    if (typeof onDraftChange !== "function" || !initialSession) return;
    if (isHydratingFromInitialSessionRef.current) {
      isHydratingFromInitialSessionRef.current = false;
      return;
    }

    const baseDate = dateObj || (initialSession.start ? new Date(initialSession.start) : null);
    const student = selectedStudent || {
      id: initialSession.studentId || "",
      name: initialSession.studentName || "",
      grade: initialSession.studentGrade || "",
    };

    if (!baseDate) {
      onDraftChange(null);
      return;
    }

    const previewStart = minutesToDate(baseDate, startMin);
    const previewEnd = minutesToDate(baseDate, endMin);
    const resolvedDayKey = isMasterMode && initialSession.dayKey
      ? initialSession.dayKey
      : dayKeyFromDate(baseDate);

    onDraftChange({
      ...initialSession,
      studentId: student.id || "",
      studentName: student.name || "",
      studentGrade: student.grade || "",
      status,
      subject,
      note,
      tutorUid,
      tutorName: tutors.find((t) => t.id === tutorUid)?.name || initialSession.tutorName || "",
      dayKey: resolvedDayKey || initialSession.dayKey || "",
      blockId: resolveBlockIdForStartMinute(dayTimeBlocks, resolvedDayKey, startMin, initialSession?.blockId),
      start: previewStart,
      end: previewEnd,
    });
  }, [
    dateObj,
    dayTimeBlocks,
    initialSession,
    isMasterMode,
    note,
    onDraftChange,
    selectedStudent,
    startMin,
    endMin,
    status,
    subject,
    tutors,
    tutorUid,
  ]);

  const handleApply = async () => {
    if (isReadOnly) return;
    if (isDayClosed) return alert("This day is marked as CLOSED. Please choose another date.");
    if (!selectedStudent) return alert("Student missing.");
    if (!dateObj || !dayKey) return alert("Invalid date selected.");
    if (!tutorUid) return alert("Please select a tutor.");
    if (endMin <= startMin) return alert("End time must be after start time.");

    const isTutorValid = availableTutors.some(t => t.id === tutorUid);
    if (!isTutorValid) {
      return alert("The selected tutor is unavailable or fully booked for this time slot. Please select a different tutor or time.");
    }

    // Auto-calculate the lane/seat behind the scenes
    const lane = resolveLane(daySessions, startMin, endMin, conflictExclusionSessionId, tutorUid);
    if (lane == null) {
      return alert("This tutor already has 3 concurrent students for that time range.");
    }

    const start = minutesToDate(dateObj, startMin);
    const end = minutesToDate(dateObj, endMin);

    const payload = {
      studentId: selectedStudent.id,
      studentName: selectedStudent.name,
      studentGrade: selectedStudent.grade,
      status,
      subject: subject.trim(),
      note: note.trim(),
      tutorUid,
      tutorName: tutors.find((t) => t.id === tutorUid)?.name || "",
      dayKey,
      blockId: resolveBlockIdForStartMinute(dayTimeBlocks, dayKey, startMin, initialSession?.blockId),
      slotIdx: lane, // Assign the calculated lane
      start,
      end,
    };

    if (isCreateMode) {
      await onCreate(payload);
      return;
    }
    await onSave(initialSession.id, payload);
  };

  // The log action only applies to a persisted session in a real (non-master)
  // schedule — master templates have no `scheduled_sessions` doc to attach a log to.
  const canOpenSessionLog =
    !isCreateMode &&
    !isMasterMode &&
    !!initialSession?.id &&
    typeof onOpenSessionLog === "function";

  return (
    <div className={isNested ? "nestedSessionEditor" : ""}>
      {isNested && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{isCreateMode ? "New Session" : "Edit Session"}</div>
          <button className="sessModal__closeBtn" onClick={onCancel} aria-label="Close" style={{ width: 26, height: 26 }}>✕</button>
        </div>
      )}

      <div style={{ marginBottom: 10, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
        {isNested ? selectedStudent?.name || "Student" : initialSession.studentName}
      </div>
      {isReadOnly && (
        <div style={{ marginBottom: 10, fontSize: 12, color: "#b45309", fontWeight: 600, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "5px 10px" }}>
          View-only — past session
        </div>
      )}

      <div className="sessModal__fields" style={{ gap: 10 }}>
        <div className="sessModal__field">
          <label className="sessModal__label">Student</label>
          <input
            type="text"
            className="sessModal__input"
            value={selectedStudent?.name || initialSession.studentName || ""}
            readOnly
            disabled
            style={{ background: "#f8fafc", color: "#64748b", cursor: "not-allowed" }}
          />
        </div>

        <div className="sessModal__field">
          <label className="sessModal__label">Status</label>
          <select className="sessModal__select" value={status} onChange={(e) => setStatus(e.target.value)} disabled={isReadOnly}>
            {SESSION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="sessModal__field">
          <label className="sessModal__label">Subject</label>
          <SubjectSelect value={subject} onChange={(val) => setSubject(val)} disabled={isReadOnly} inputClassName="sessModal__input" />
        </div>

        {!isMasterMode && (
          <div className="sessModal__field">
            <label className="sessModal__label">Date</label>
            <input
              type="date"
              className={`sessModal__input${isDayClosed ? " sessModal__input--error" : ""}`}
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              disabled={isReadOnly}
            />
            {isDayClosed && <div className="sessModal__closedBanner">This day is closed.</div>}
          </div>
        )}

        <div className="sessModal__field">
          <label className="sessModal__label">Time</label>
          <div className="sessModal__timeRow">
            <select className="sessModal__select" value={startMin} onChange={(e) => setStartMin(Number(e.target.value))} disabled={isReadOnly}>
              {timeOptions.map((t) => <option key={t} value={t}>{formatTimeFromMinutes(t)}</option>)}
            </select>
            <select className="sessModal__select" value={endMin} onChange={(e) => setEndMin(Number(e.target.value))} disabled={isReadOnly}>
              {endOptions.map((t) => <option key={t} value={t}>{formatTimeFromMinutes(t)}</option>)}
            </select>
          </div>
        </div>

        <div className="sessModal__field">
          <label className="sessModal__label">Tutor</label>
          <select className="sessModal__select" value={tutorUid} onChange={(e) => setTutorUid(e.target.value)} disabled={isReadOnly}>
            {isDayClosed ? (
              <option value="">— Closed —</option>
            ) : (
              <>
                <option value="">Select tutor…</option>
                {availableTutors.map((t) => (
                  <option key={t.id} value={t.id}>{t.name || t.email} ({t.seatsLeft} left)</option>
                ))}
                {tutorUid && !availableTutors.find((at) => at.id === tutorUid) && (
                  <option value={tutorUid}>
                    {tutors.find((t) => t.id === tutorUid)?.name || "Current"} (unavailable)
                  </option>
                )}
              </>
            )}
          </select>
        </div>

        <div className="sessModal__field">
          <label className="sessModal__label">Short Note</label>
          <textarea className="sessModal__textarea" value={note} onChange={(e) => setNote(e.target.value)} readOnly={isReadOnly} placeholder="Optional note…" />
        </div>
      </div>

      <div className="sessModal__actions">
        {!isCreateMode && !isReadOnly && (
          <button
            className="sessModal__btnDanger"
            onClick={() => { if (window.confirm("Delete this session?")) onDelete(initialSession.id); }}
          >
            Delete
          </button>
        )}
        {/* Session log — available for real, saved sessions only. Deliberately NOT
            gated on isReadOnly: past sessions are locked for editing, and those are
            exactly the sessions an admin needs to add a log for. */}
        {canOpenSessionLog && (
          <button
            className={`sessModal__btnLog ${hasSessionLog ? "sessModal__btnLog--done" : ""}`.trim()}
            onClick={() => onOpenSessionLog(initialSession)}
            title={
              hasSessionLog
                ? "A log already exists for this session — open it to review or edit"
                : "Add a session log for this session on behalf of the tutor"
            }
          >
            <FiFileText size={13} />
            {hasSessionLog ? "View Session Log" : "Add Session Log"}
          </button>
        )}
        <button className="sessModal__btnCancel" onClick={onCancel}>Cancel</button>
        <button
          className="sessModal__btnPrimary"
          disabled={isDayClosed || isReadOnly}
          onClick={handleApply}
        >
          {isCreateMode ? "Create" : "Apply"}
        </button>
      </div>
    </div>
  );
}

export default function EditSessionModal({
  isOpen,
  onClose,
  session,
  tutors,
  students,
  onApplyPatch,
  onDelete,
  onCreateSession,
  loadStudentSessionsForRange,
  isMasterMode = false,
  masterAvailability = [],
  masterDayConfigs = {},
  allWeekSessions = [],
  isDateEditable = () => true,
  onOpenSessionLog = null,
  sessionLogBySessionId = null,
}) {
  const [activePanel, setActivePanel] = useState({ kind: "edit", data: null });
  const [drawerDraft, setDrawerDraft] = useState(null);
  // Track which session chip is highlighted in the calendar
  const [calendarSelectedSessionId, setCalendarSelectedSessionId] = useState(null);

  const isStudentCalendarMode = !isMasterMode;

  const canEditDate = useCallback(
    (value) => (typeof isDateEditable === "function" ? !!isDateEditable(value) : true),
    [isDateEditable]
  );

  useEffect(() => {
    if (isOpen && session) {
      setActivePanel({ kind: "edit", data: session });
      setDrawerDraft(session);
      setCalendarSelectedSessionId(session?.id || null);
    }
  }, [isOpen, session]);

  const editorBaseSession = activePanel?.data || session;
  const selectedDrawerSession = drawerDraft || editorBaseSession;
  const isCreateDrawer = activePanel?.kind === "create";
  const drawerReadOnly =
    !isCreateDrawer &&
    !!selectedDrawerSession?.start &&
    !canEditDate(selectedDrawerSession.start);

  const openCreateDraftForDate = (date) => {
    // ModalCalendarPanel already filters closed days before calling onDayClick
    if (!isStudentCalendarMode || typeof onCreateSession !== "function") return;
    if (!canEditDate(date)) return;
    const sourceStart = session.start ? new Date(session.start) : new Date();
    const sourceEnd = session.end ? new Date(session.end) : new Date(sourceStart.getTime() + 60 * 60 * 1000);
    const duration = Math.max(5, minutesFromDate(sourceEnd) - minutesFromDate(sourceStart));

    const start = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      sourceStart.getHours(),
      sourceStart.getMinutes(),
      0,
      0
    );
    const end = new Date(start.getTime() + duration * 60 * 1000);

    const nextDraft = {
      studentId: session.studentId,
      studentName: session.studentName,
      studentGrade: session.studentGrade,
      status: "Pending",
      subject: session.subject || "",
      note: "",
      tutorUid: session.tutorUid || "",
      tutorName: session.tutorName || "",
      start,
      end,
      dayKey: dayKeyFromDate(date),
      blockId: session.blockId || "",
      slotIdx: 0,
    };

    setCalendarSelectedSessionId(null);
    setActivePanel({
      kind: "create",
      data: nextDraft,
    });
    setDrawerDraft(nextDraft);
  };

  const resetDrawerToPrimary = () => {
    setActivePanel({ kind: "edit", data: session });
    setDrawerDraft(session);
    setCalendarSelectedSessionId(session?.id || null);
  };

  const handleDrawerCancel = () => {
    if (isCreateDrawer) {
      resetDrawerToPrimary();
      return;
    }
    if (selectedDrawerSession?.id && selectedDrawerSession.id !== session.id) {
      resetDrawerToPrimary();
      return;
    }
    onClose();
  };

  const handleDrawerSave = async (id, payload) => {
    if (payload?.start && !canEditDate(payload.start)) return;
    onClose();
    await onApplyPatch(id, payload);
  };

  const handleDrawerDelete = async (id) => {
    if (selectedDrawerSession?.start && !canEditDate(selectedDrawerSession.start)) return;
    onClose();
    await onDelete(id);
  };

  const handleDrawerCreate = async (payload) => {
    if (typeof onCreateSession !== "function") return;
    if (payload?.start && !canEditDate(payload.start)) return;
    onClose();
    await onCreateSession(payload);
  };

  const handleCalendarMoveDraft = useCallback((nextSession) => {
    if (!nextSession?.id) return;
    setCalendarSelectedSessionId(nextSession.id);
    setActivePanel({ kind: "edit", data: nextSession });
    setDrawerDraft(nextSession);
  }, []);

  const handleCalendarPasteDraft = useCallback((nextSession) => {
    if (!nextSession) return;
    setCalendarSelectedSessionId(null);
    setActivePanel({ kind: "create", data: nextSession });
    setDrawerDraft(nextSession);
  }, []);

  if (!isOpen || !session) return null;

  return (
    <div className="evtModalOverlay" {...backdropDismiss(onClose)}>
      <div className="sessModal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sessModal__header">
          <div className="sessModal__title">
            Edit Session{isMasterMode ? " — Master Schedule" : ""}
            {session?.studentName ? ` · ${session.studentName}` : ""}
          </div>
          <button className="sessModal__closeBtn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="sessModal__body">
          {/* ── Calendar Panel — real Student Calendar experience ── */}
          <div className="sessModal__calPanel">
            <div className="sessModal__calHeader">
              <div className="sessModal__calTitle">
                {session?.studentName ? `${session.studentName}'s Calendar` : "Student Calendar"}
              </div>
              <div className="sessModal__calHint">
                Click a session chip to edit it. Click an open day to create a new draft.
              </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              <ModalCalendarPanel
                studentId={session?.studentId || null}
                studentName={session?.studentName || ""}
                selectedDate={
                  selectedDrawerSession?.start
                    ? new Date(selectedDrawerSession.start)
                    : null
                }
                selectedSessionId={calendarSelectedSessionId}
                draftSession={selectedDrawerSession}
                allowSessionTransfers={isStudentCalendarMode}
                onDayClick={openCreateDraftForDate}
                onMoveSessionDraft={handleCalendarMoveDraft}
                onPasteSessionDraft={handleCalendarPasteDraft}
                onSessionClick={(sessId, sess) => {
                  setCalendarSelectedSessionId(sessId);
                  if (sess) {
                    setActivePanel({ kind: "edit", data: sess });
                    setDrawerDraft(sess);
                  }
                }}
                tutors={tutors}
              />
            </div>
          </div>

          {/* ── Form Drawer Panel ── */}
          <div className="sessModal__formPanel">
            <div className="sessModal__formHeader">
              <div className="sessModal__formTitle">{isCreateDrawer ? "New Session" : "Edit Session"}</div>
              <div className="sessModal__formSub">
                {selectedDrawerSession?.start
                  ? formatLongDate(new Date(selectedDrawerSession.start))
                  : "Select a session or day"}
              </div>
            </div>

            <SessionEditor
              initialSession={editorBaseSession}
              students={students}
              tutors={tutors}
              isMasterMode={isMasterMode}
              masterAvailability={masterAvailability}
              masterDayConfigs={masterDayConfigs}
              allWeekSessions={allWeekSessions}
              onDraftChange={setDrawerDraft}
              onCreate={isCreateDrawer ? handleDrawerCreate : null}
              onSave={handleDrawerSave}
              onDelete={handleDrawerDelete}
              onCancel={handleDrawerCancel}
              isReadOnly={drawerReadOnly}
              onOpenSessionLog={isMasterMode ? null : onOpenSessionLog}
              hasSessionLog={!!sessionLogBySessionId?.[editorBaseSession?.id]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
